/**
 * <tbyb-trial-limit>
 * ---------------------------------------------------------------------------
 * Wraps the buy buttons of a "Try Before You Buy" (trial) product.
 *
 * A trial product is a single test unit we ship, so it must never be ordered
 * more than once. Rather than disabling the button up front, we let it look
 * normal and only react when the customer tries to add the product a second
 * time: the add is blocked and a highlighted note is revealed.
 *
 * The element tracks whether the trial product is already in the cart, using
 * the initial server-rendered state (data-in-cart) and the cart events the
 * theme already dispatches (`cart:change` carries the full cart, `cart:refresh`
 * is a generic update signal), so it stays correct after AJAX add/remove and
 * across the cart drawer / quick-buy modal.
 */
if (!window.customElements.get('tbyb-trial-limit')) {
  class TbybTrialLimit extends HTMLElement {
    connectedCallback() {
      this.handle = this.getAttribute('data-product-handle');
      this.inCart = this.hasAttribute('data-in-cart');

      this._onCartChange = (event) => this.syncFromCart(event.detail && event.detail.cart);
      this._onCartRefresh = () => this.fetchAndSync();
      this._onClick = (event) => this.handleClick(event);

      document.addEventListener('cart:change', this._onCartChange);
      document.addEventListener('cart:refresh', this._onCartRefresh);

      // Capture phase so we can block the form submission before the theme's
      // product-form handler runs and adds the item again.
      const button = this.querySelector('[type="submit"]');
      if (button) button.addEventListener('click', this._onClick, true);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChange);
      document.removeEventListener('cart:refresh', this._onCartRefresh);

      const button = this.querySelector('[type="submit"]');
      if (button) button.removeEventListener('click', this._onClick, true);
    }

    handleClick(event) {
      if (!this.inCart) return; // first add: let it proceed normally
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showNote();
    }

    syncFromCart(cart) {
      if (!cart || !Array.isArray(cart.items)) {
        this.fetchAndSync();
        return;
      }

      this.inCart = cart.items.some((item) => {
        const itemHandle = item.handle || (item.product && item.product.handle);
        return itemHandle === this.handle;
      });

      this.toggleAttribute('data-in-cart', this.inCart);

      // If the trial unit was removed from the cart, the note no longer applies.
      if (!this.inCart) this.hideNote();
    }

    async fetchAndSync() {
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const cart = await response.json();
        this.syncFromCart(cart);
      } catch (error) {
        /* network error - keep the current state */
      }
    }

    get note() {
      return this.querySelector('[data-tbyb-note]');
    }

    showNote() {
      const note = this.note;
      if (!note) return;
      // Re-trigger the entrance animation even if it was already shown.
      note.hidden = false;
      note.style.animation = 'none';
      void note.offsetWidth;
      note.style.animation = '';
    }

    hideNote() {
      if (this.note) this.note.hidden = true;
    }
  }

  window.customElements.define('tbyb-trial-limit', TbybTrialLimit);
}
